import { WorkspaceRole, UpgradeInfo } from "./workspace.types";
import { Permissions } from "utils/permissions/permissionConstants";

/**
 * Get upgrade message based on permission and role requirements
 */
export const getUpgradeMessage = (
  permission?: string,
  requiredRole?: WorkspaceRole,
  currentRole?: WorkspaceRole,
  customMessage?: string
): string => {
  if (customMessage) return customMessage;

  // Role-based messages
  if (requiredRole && currentRole) {
    const roleMessages: Record<WorkspaceRole, string> = {
      [WorkspaceRole.OWNER]: "Owner access required. Contact your workspace admin.",
      [WorkspaceRole.ADMIN]: "Admin access required. Contact your workspace admin.",
      [WorkspaceRole.MEMBER]: "Member access required. Contact your workspace admin.",
      [WorkspaceRole.GUEST]: "Guest access required. Contact your workspace admin.",
      [WorkspaceRole.CUSTOM]: "Custom role access required. Contact your workspace admin.",
    };
    return roleMessages[requiredRole];
  }

  // Permission-based messages
  const permissionMessages: Record<string, string> = {
    [Permissions.WORKSPACE_READ]: "Access denied. Contact your workspace admin.",
    [Permissions.WORKSPACE_UPDATE]: "Editor access required. Contact your workspace admin.",
    [Permissions.WORKSPACE_MEMBER_INVITE]: "Admin access required. Contact your workspace admin.",
    [Permissions.WORKSPACE_MEMBER_UPDATE]: "Admin access required. Contact your workspace admin.",
    [Permissions.WORKSPACE_MEMBER_REMOVE]: "Admin access required. Contact your workspace admin.",
    [Permissions.WORKSPACE_BILLING_READ]: "Owner access required for billing.",
    [Permissions.WORKSPACE_BILLING_UPDATE]: "Owner access required for billing.",
    [Permissions.WORKSPACE_PROJECT_CREATE]: "Permission required. Contact your workspace admin.",
    [Permissions.PROJECT_READ]: "Permission required. Contact your workspace admin.",
  };

  return (
    permissionMessages[permission || Permissions.WORKSPACE_READ] ||
    "Permission required. Contact your workspace admin."
  );
};

/**
 * Get required plan for a permission
 */
export const getRequiredPlan = (permission: string): string => {
  const planRequirements: Record<string, string> = {
    [Permissions.WORKSPACE_READ]: "Free",
    [Permissions.WORKSPACE_UPDATE]: "Pro",
    [Permissions.WORKSPACE_MEMBER_INVITE]: "Pro",
    [Permissions.WORKSPACE_MEMBER_UPDATE]: "Pro",
    [Permissions.WORKSPACE_MEMBER_REMOVE]: "Pro",
    [Permissions.WORKSPACE_BILLING_READ]: "Pro",
    [Permissions.WORKSPACE_BILLING_UPDATE]: "Pro",
    [Permissions.WORKSPACE_PROJECT_CREATE]: "Pro",
    [Permissions.PROJECT_READ]: "Free",
  };

  return planRequirements[permission] || "Pro";
};

/**
 * Get features unlocked by a permission
 */
export const getPermissionFeatures = (permission: string): string[] => {
  const featureMap: Record<string, string[]> = {
    [Permissions.WORKSPACE_READ]: ["View workspace details", "Access workspace projects"],
    [Permissions.WORKSPACE_UPDATE]: ["Edit workspace settings", "Update workspace information"],
    [Permissions.WORKSPACE_MEMBER_LIST]: ["View workspace members", "View member activity"],
    [Permissions.WORKSPACE_MEMBER_INVITE]: ["Invite team members"],
    [Permissions.WORKSPACE_MEMBER_UPDATE]: ["Manage member roles"],
    [Permissions.WORKSPACE_MEMBER_REMOVE]: ["Remove members"],
    [Permissions.WORKSPACE_BILLING_READ]: ["View billing settings", "View usage analytics"],
    [Permissions.WORKSPACE_BILLING_UPDATE]: ["Manage billing settings", "Update payment methods"],
    [Permissions.WORKSPACE_PROJECT_CREATE]: [
      "Create new projects",
      "Set project permissions",
      "Manage project settings",
    ],
    [Permissions.PROJECT_READ]: [
      "View workspace projects",
      "Access shared projects",
      "View project analytics",
    ],
  };

  return featureMap[permission] || ["Access to this feature"];
};

/**
 * Get upgrade action type based on permission and role
 */
export const getUpgradeAction = (
  permission: string,
  requiredRole?: WorkspaceRole
): "upgrade" | "contact_admin" | "request_access" => {
  // Role-based actions
  if (requiredRole === WorkspaceRole.OWNER) {
    return "contact_admin";
  }

  if (requiredRole === WorkspaceRole.ADMIN) {
    return "request_access";
  }

  // Permission-based actions
  const upgradePermissions: string[] = [
    Permissions.WORKSPACE_UPDATE,
    Permissions.WORKSPACE_MEMBER_INVITE,
    Permissions.WORKSPACE_MEMBER_UPDATE,
    Permissions.WORKSPACE_MEMBER_REMOVE,
    Permissions.WORKSPACE_PROJECT_CREATE,
  ];

  if (upgradePermissions.includes(permission)) {
    return "upgrade";
  }

  return "request_access";
};

/**
 * Create upgrade info object
 */
export const createUpgradeInfo = (
  permission: string,
  requiredRole?: WorkspaceRole,
  currentRole?: WorkspaceRole,
  currentPlan?: string
): UpgradeInfo => {
  const requiredPlan = getRequiredPlan(permission);
  const action = getUpgradeAction(permission, requiredRole);

  return {
    message: getUpgradeMessage(permission, requiredRole, currentRole),
    action,
    requiredPlan,
    currentPlan: currentPlan || "Free",
    features: getPermissionFeatures(permission),
    permission,
    requiredRole,
  };
};

/**
 * Check if user can upgrade (not already on highest plan)
 */
export const canUpgrade = (currentPlan: string, requiredPlan: string): boolean => {
  const planHierarchy = ["Free", "Pro", "Enterprise"];
  const currentIndex = planHierarchy.indexOf(currentPlan);
  const requiredIndex = planHierarchy.indexOf(requiredPlan);

  return currentIndex < requiredIndex;
};
